import { useMemo } from 'react';
import { Box, FormControl, FormHelperText, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { scanStepFunctionDefinitionResources } from '../resources';

type UiOptions = {
  /**
   * Field name in the scaffolder formData that contains the Step Function definition JSON string.
   * Defaults to "step_function_definition_json".
   */
  definitionFieldName?: string;
};

type Props = FieldExtensionComponentProps<string, UiOptions>;

const DEFAULT_DEFINITION_FIELD = 'step_function_definition_json';

function renderBullets(items: string[], limit = 25) {
  if (items.length === 0) return null;
  const shown = items.slice(0, limit);
  const remaining = items.length - shown.length;
  return (
    <ul style={{ margin: '6px 0 0 18px' }}>
      {shown.map(v => (
        <li key={v} style={{ marginBottom: 2 }}>
          <code>{v}</code>
        </li>
      ))}
      {remaining > 0 && <li>…and {remaining} more</li>}
    </ul>
  );
}

export const StepFunctionResourcesSummary = (props: Props) => {
  const { schema, uiSchema, formContext } = props;

  const title = schema.title || uiSchema?.['ui:title'] || 'Detected resources (from definition)';
  const description =
    schema.description ||
    uiSchema?.['ui:description'] ||
    'Best-effort scan of the definition to help select IAM policies. This may miss runtime-determined resources.';

  const definitionFieldName =
    uiSchema?.['ui:options']?.definitionFieldName ?? DEFAULT_DEFINITION_FIELD;

  const rootFormData = (formContext as any)?.formData as Record<string, unknown> | undefined;
  const definitionText = String(rootFormData?.[definitionFieldName] ?? '');

  const inventory = useMemo(
    () => scanStepFunctionDefinitionResources(definitionText),
    [definitionText],
  );

  if (!definitionText.trim()) {
    return (
      <FormControl fullWidth>
        <Box mb={1}>
          <Typography variant="subtitle2">{title}</Typography>
          {description && <FormHelperText>{description}</FormHelperText>}
        </Box>
        <Alert severity="info">
          Paste a definition to see a best-effort list of referenced resources.
        </Alert>
      </FormControl>
    );
  }

  const hasAny =
    inventory.stateMachineArns.length > 0 ||
    inventory.dynamoTableNames.length > 0 ||
    inventory.s3Buckets.length > 0 ||
    inventory.serviceIntegrations.length > 0 ||
    inventory.otherArns.length > 0;

  return (
    <FormControl fullWidth>
      <Box mb={1}>
        <Typography variant="subtitle2">{title}</Typography>
        {description && <FormHelperText>{description}</FormHelperText>}
      </Box>

      {inventory.parseError && (
        <Alert severity="info" style={{ marginBottom: 8 }}>
          Definition JSON is not valid yet; showing regex-based results only ({inventory.parseError}
          ).
        </Alert>
      )}

      {!hasAny && (
        <Alert severity="info">
          No obvious resources detected. If your workflow uses dynamic names or non-ARN values, this
          scan may not find them.
        </Alert>
      )}

      {inventory.stateMachineArns.length > 0 && (
        <Alert severity="success" style={{ marginBottom: 8 }}>
          <div>Step Functions</div>
          {renderBullets(inventory.stateMachineArns)}
        </Alert>
      )}
      {inventory.dynamoTableNames.length > 0 && (
        <Alert severity="success" style={{ marginBottom: 8 }}>
          <div>DynamoDB Tables</div>
          {renderBullets(inventory.dynamoTableNames)}
        </Alert>
      )}
      {inventory.s3Buckets.length > 0 && (
        <Alert severity="success" style={{ marginBottom: 8 }}>
          <div>S3 Buckets</div>
          {renderBullets(inventory.s3Buckets)}
        </Alert>
      )}
      {inventory.serviceIntegrations.length > 0 && (
        <Alert severity="success" style={{ marginBottom: 8 }}>
          <div>Service Integrations</div>
          {renderBullets(inventory.serviceIntegrations)}
        </Alert>
      )}

      {inventory.otherArns.length > 0 && (
        <Alert severity="success" style={{ marginBottom: 8 }}>
          <div>Other ARNs</div>
          {renderBullets(inventory.otherArns)}
        </Alert>
      )}
    </FormControl>
  );
};

// Field extension registration moved to plugin.ts following Backstage best practices
